/* eslint-disable  @typescript-eslint/no-non-null-assertion */

import React, { Suspense, useContext, useEffect, useMemo } from 'react'
import './App.css'
import { authContext } from './components/CognitoContextProvider/CognitoContextProvider'
import { Navigate, Route, Routes, useNavigate } from 'react-router'
import Loader from './components/Loader/Loader'
import Login from './containers/Login/Login'
import { User } from './types/User'
import Home from './containers/Home/Home'
import Header from './components/Header/Header'
import { ToastProvider } from './components/Toast/ToastProvider'
import { toast } from './utils/toast'
import CreateCompany from './containers/CreateCompany/CreateCompany'
import TribesView from './containers/Tribes/TribesView'
import GuildView from './containers/Guilds/GuildView'
import ClientsView from './containers/ClientsView/ClientsView'
import ClientDetails from './containers/ClientDetails/ClientDetails'
import EditEmployee from './containers/EditEmployee/EditEmployee'
import AddEmployee from './containers/AddEmployee/AddEmployee'
import EmployeesView from './containers/EmployeesView/EmployeesView'
import EditTribe from './containers/Tribes/EditTribe'
import AddTimeOffs from './containers/TimeOff/AddTimeOff'
import EngagementsView from './containers/Engagements/Engagements'
import EngagementCreate from './containers/Engagements/EngagementCreate'
import EngagementEdit from './containers/Engagements/EngagementEdit'
import TimeOffReport from './containers/TimeOffReport/TimeOffReport'
import Footer from './components/Footer/Footer'
import TribeOrgPage from './containers/TribeOrg/TribeOrg'
import ReleasePlans from './components/ReleasePlans'
import GrowthWheel from './containers/GrowthWheel/GrowthWheel'
import GrowthWheelAdmin from './containers/GrowthWheelAdmin/GrowthWheelAdmin'
import GrowthWheelEditor from './containers/GrowthWheelEditor/GrowthWheelEditor'
import GrowthWheelEditorDetail from './containers/GrowthWheelEditor/GrowthWheelEditorDetail'
import { useGetAuthenticatedUserProfileQuery } from './redux/user.endpoints'
import ProfileSetupPending from './containers/ProfileSetupPending/ProfileSetupPending'
import { isProfileIncomplete } from './utils/profileUtils'

function App() {
  const navigate = useNavigate()
  const { auth, logOut } = useContext(authContext)

  const idToken = useMemo(() => {
    if (!auth.data?.signInUserSession?.idToken?.jwtToken) return ''
    return auth.data.signInUserSession.idToken.jwtToken
  }, [auth.data])

  const shouldFetchProfile =
    !auth.loading && Boolean(auth.data) && Boolean(idToken)

  const {
    data: profileData,
    isLoading: isProfileQueryLoading,
    isError: isProfileQueryError,
  } = useGetAuthenticatedUserProfileQuery(idToken, {
    skip: !shouldFetchProfile,
  })

  const loggedProfile: User | undefined = shouldFetchProfile
    ? profileData
    : undefined

  const profileLoading =
    auth.loading || (shouldFetchProfile && isProfileQueryLoading)

  const isAdmin = !profileLoading && loggedProfile && loggedProfile.isAdmin

  const profileIncomplete =
    !profileLoading && loggedProfile && isProfileIncomplete(loggedProfile)

  useEffect(() => {
    if (!isProfileQueryError || !shouldFetchProfile) return
    const run = async () => {
      toast.error('There was a problem logging in. Contact an administrator.', {
        toastId: 'get-profile-error',
      })
      await logOut()
      navigate('/')
    }
    void run()
  }, [isProfileQueryError, shouldFetchProfile, logOut, navigate])

  return (
    <ToastProvider>
      <div className="app-shell">
        <div className="main-container">
          {loggedProfile && <Header user={loggedProfile} />}
          <Routes>
            {profileLoading && <Route path="*" element={<Loader />} />}
            {!profileLoading && !loggedProfile && (
              <>
                <Route
                  path="/login"
                  element={
                    <Suspense fallback={<Loader />}>
                      <Login />
                    </Suspense>
                  }
                />
                <Route
                  path="/register-company"
                  element={
                    <Suspense fallback={<Loader />}>
                      <CreateCompany />
                    </Suspense>
                  }
                />
                <Route path="*" element={<Navigate to="/login" />} />
              </>
            )}
            {isAdmin && (
              <>
                <Route path="/members">
                  <Route
                    path=""
                    element={
                      <Suspense fallback={<Loader />}>
                        <EmployeesView companyId={loggedProfile.company.id} />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/members/new"
                    element={
                      <AddEmployee companyId={loggedProfile.company.id} />
                    }
                  />
                  <Route
                    path="/members/:userId"
                    element={
                      <EditEmployee companyId={loggedProfile.company.id} />
                    }
                  />
                </Route>
                <Route path="/engagements">
                  <Route
                    path=""
                    element={
                      <Suspense fallback={<Loader />}>
                        <EngagementsView companyId={loggedProfile.company.id} />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/engagements/create"
                    element={
                      <EngagementCreate companyId={loggedProfile.company.id} />
                    }
                  />
                  <Route
                    path="/engagements/:engagementId"
                    element={
                      <EngagementEdit companyId={loggedProfile.company.id} />
                    }
                  />
                </Route>
                <Route
                  path="/tribes"
                  element={<TribesView user={loggedProfile} />}
                />
                <Route
                  path="/tribes/:tribeId"
                  element={<EditTribe user={loggedProfile} />}
                />
                <Route path="/guilds" element={<GuildView />} />
                <Route
                  path="/clients"
                  element={<ClientsView user={loggedProfile} />}
                />
                <Route
                  path="/clients/:clientId"
                  element={<ClientDetails user={loggedProfile} />}
                />
                <Route
                  path="/reports"
                  element={
                    <Suspense fallback={<Loader />}>
                      <TimeOffReport
                        profile={loggedProfile}
                        companyId={loggedProfile.company.id}
                      />
                    </Suspense>
                  }
                />
                <Route path="/releaseplans" element={<ReleasePlans />} />
              </>
            )}
            {profileIncomplete && (
              <Route path="*" element={<ProfileSetupPending />} />
            )}
            {loggedProfile && !profileIncomplete && (
              <>
                <Route
                  path="/time-off"
                  element={
                    <Suspense fallback={<Loader />}>
                      <Home profile={loggedProfile} />
                    </Suspense>
                  }
                />
                <Route
                  path="/team-time-off"
                  element={
                    <Suspense fallback={<Loader />}>
                      <Home profile={loggedProfile} />
                    </Suspense>
                  }
                />
                <Route path="/" element={<Navigate to="/time-off" replace />} />
                <Route
                  path="/growth-wheel"
                  element={
                    <Suspense fallback={<Loader />}>
                      <GrowthWheel user={loggedProfile} />
                    </Suspense>
                  }
                />
                {loggedProfile.isGrowthWheelReviewer && (
                  <Route
                    path="/growth-wheel-admin"
                    element={
                      <Suspense fallback={<Loader />}>
                        <GrowthWheelAdmin user={loggedProfile} />
                      </Suspense>
                    }
                  />
                )}
                {loggedProfile.isGrowthWheelEditor && (
                  <>
                    <Route
                      path="/edit-growth-wheel"
                      element={
                        <Suspense fallback={<Loader />}>
                          <GrowthWheelEditor user={loggedProfile} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/edit-growth-wheel/:growthWheelId"
                      element={
                        <Suspense fallback={<Loader />}>
                          <GrowthWheelEditorDetail />
                        </Suspense>
                      }
                    />
                  </>
                )}
                <Route
                  path="/add-timeoff"
                  element={<AddTimeOffs user={loggedProfile} />}
                />
                <Route
                  path="/tribeOrg"
                  element={
                    <Suspense fallback={<Loader />}>
                      <TribeOrgPage
                        profile={loggedProfile}
                        companyId={loggedProfile.company.id}
                        readOnly={!loggedProfile.isAdmin}
                      />
                    </Suspense>
                  }
                />
              </>
            )}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
        <Footer user={loggedProfile} />
      </div>
    </ToastProvider>
  )
}

export default App
